import React, { memo, useCallback, useState, useEffect } from 'react'
import { makeStyles, useTheme } from '@material-ui/styles'
import classNames from 'classnames'
import { Grid, Theme } from '@material-ui/core'
import Hidden from '@material-ui/core/Hidden'
import Button from '@material-ui/core/Button'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import InfiniteScroll from 'react-infinite-scroller'
import { useLocation, matchPath, useHistory } from 'react-router-dom'
import ClassNames from 'classnames'

import {
	Loading,
	JobApplicantsFeed,
	JobApplicantsFilterSection,
} from 'components'
import generalStyles from '../../styles'
import styles from './styles'
import { KeyboardArrowLeft } from '@material-ui/icons'
import { StoreValue } from 'store'
import { ConditionalWrapper, combineStyles } from 'utils'
import { SearchEmptyResult } from '..'
import { ECompanyActionTypes } from 'models'
import { useFeedJobApplicantsQuery } from 'graphql/__generated__/graphql'

const combinedStyles: any = combineStyles(generalStyles, styles)
const useStyles = makeStyles(combinedStyles)

const JobApplicantsView: React.FunctionComponent<any> = ({
	showJobs = true,
	storeSearchDebounceQuery,
	jobId,
	storeDispatch,
}) => {
	const location = useLocation()
	const history = useHistory()
	const isCompanySettingsPage = matchPath(location.pathname, {
		path: [
			'/:language(en|ro)/employers/:companySlug/company-settings',
			'/:language(en|ro)/employers/:companySlug/active-jobs',
		],
		exact: false,
	})
	const disableBackButtonOnSettingsPage = matchPath(location.pathname, {
		path: '/:language(en|ro)/employers/:companySlug/company-settings',
		exact: false,
	})
	const [filterData, setFilterData] = useState({
		name: '',
		applicationDateStart: '',
		applicationDateEnd: '',
	})
	const { t } = useTranslation()
	const theme: Theme = useTheme()
	const isMdDown: boolean = useMediaQuery(theme.breakpoints.down('md'))

	const classes = useStyles()
	const [isFilterVisible, setIsFilterVisible] = React.useState(false)
	const {
		data: feedJobApplicantsData,
		loading,
		fetchMore: fetchFeedJobApplicants,
	} = useFeedJobApplicantsQuery({
		fetchPolicy: 'network-only',
		variables: {
			first: 10,
			after: null,
			jobId,
			applicantName: filterData.name,
			applicationDateStart: filterData.applicationDateStart,
			applicationDateEnd: filterData.applicationDateEnd,
		},
	})
	const jobApplicants = feedJobApplicantsData?.feedJobApplicants?.edges?.map(
		edge => edge.node,
	)
	feedJobApplicantsData && console.log({ feedJobApplicantsData })
	const hasNextPage =
		feedJobApplicantsData?.feedJobApplicants?.pageInfo.hasNextPage

	const showFilter = useCallback(() => {
		setIsFilterVisible(true)
	}, [])

	const handleFilterData = useCallback(
		(
			type: 'name' | 'applicationDateStart' | 'applicationDateEnd',
			data: string,
		) => {
			setFilterData({ ...filterData, [type]: data })
		},
		[filterData],
	)

	const closeFilter = useCallback(() => {
		setIsFilterVisible(false)
	}, [])

	const handleGoBack = useCallback(() => {
		// refresh page
		history.go(0)
	}, [history])

	const toggleDrawer = useCallback(
		(open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
			if (
				event &&
				event.type === 'keydown' &&
				((event as React.KeyboardEvent).key === 'Tab' ||
					(event as React.KeyboardEvent).key === 'Shift')
			) {
				return
			}
			setIsFilterVisible(open)
		},
		[],
	)

	useEffect(() => {
		return () => {
			storeDispatch({
				type: ECompanyActionTypes.setCompanyBreadcrumbPath,
				path: null,
			})
			storeDispatch({
				type: ECompanyActionTypes.setCompanySettingsJobApplicantsJobId,
				jobId: null,
			})
		}
	}, [storeDispatch])

	return (
		<Grid
			container
			justifyContent="center"
			style={{ marginTop: 35 }}
			className={ClassNames(classes.container, {
				[classes.relativeContainer]: isCompanySettingsPage?.isExact,
			})}
		>
			<ConditionalWrapper
				condition={showJobs}
				wrapper={(children: any) => (
					<Grid
						className={classes.jobsContainer}
						container={true}
						direction="row"
					>
						{!loading && (
							<Grid
								className={classNames([
									classes.jobsMainContent,
								])}
								container={true}
								direction="column"
							>
								<div className={classes.filterButton}>
									{/* <Hidden only={['lg', 'xl']}> */}
									{/* If path is on company-settings, back button it`s hidden */}
									{!disableBackButtonOnSettingsPage && (
										<Button
											className={classes.backBtn}
											onClick={() => handleGoBack()}
										>
											<KeyboardArrowLeft />
											<span
												className={classes.backBtnText}
											>
												{t('Back')}
											</span>
										</Button>
									)}
									<Button
										classes={{
											root: classes.filterSectionButton,
										}}
										variant="contained"
										color="primary"
										onClick={toggleDrawer(true)}
									>
										{t('Filter')}
									</Button>
									{/* </Hidden> */}
								</div>
							</Grid>
						)}
						{children}
					</Grid>
				)}
			>
				{loading && (
					<Loading size={48} style={{ margin: '50px auto' }} />
				)}
				{!loading && jobApplicants?.length === 0 && (
					<SearchEmptyResult query={storeSearchDebounceQuery} />
				)}
				{!loading && jobApplicants && jobApplicants?.length > 0 && (
					<div
						className={classNames(
							classes.tabsContentContainer,
							classes.tabsContentContainerCompany,
							classes.fixFlexList,
						)}
					>
						<InfiniteScroll
							pageStart={0}
							loadMore={() =>
								fetchFeedJobApplicants({
									variables: {
										data: {
											jobId: 'ckbriyx8500003h60fayf8hlb',

											first: 5,
											after: feedJobApplicantsData
												?.feedJobApplicants?.edges[
												feedJobApplicantsData
													?.feedJobApplicants?.edges
													.length - 1
											].cursor,
										},
									},
									updateQuery: (
										previousQueryResult,
										{ fetchMoreResult },
									) => {
										if (
											!fetchMoreResult ||
											!fetchMoreResult.feedJobApplicants
										) {
											return previousQueryResult
										}

										const { edges: newEdges, pageInfo } =
											fetchMoreResult.feedJobApplicants

										return newEdges.length > 0
											? {
													feedJobApplicants: {
														__typename:
															previousQueryResult
																.feedJobApplicants
																?.__typename,
														edges: [
															...(previousQueryResult
																.feedJobApplicants
																?.edges || []),
															...newEdges,
														],
														pageInfo,
													},
											  }
											: previousQueryResult
									},
								})
							}
							hasMore={hasNextPage}
							loader={
								<div
									style={{ flexGrow: 1, display: 'flex' }}
									key={'loading'}
								>
									<Loading
										size={48}
										style={{ margin: '50px auto' }}
									/>
								</div>
							}
							className={classes.searchContentCards}
						>
							{jobApplicants && (
								<JobApplicantsFeed
									jobApplicantsData={jobApplicants}
								/>
							)}
						</InfiniteScroll>
					</div>
				)}
			</ConditionalWrapper>
			{!isCompanySettingsPage ? (
				<>
					<Hidden mdDown>
						<div
							className={classNames(classes.filterSection, {
								[classes.filterSectionWithSmallerTop]:
									isCompanySettingsPage,
							})}
						>
							<JobApplicantsFilterSection
								handleFilterData={handleFilterData}
								filterData={filterData}
							/>
						</div>
					</Hidden>
					{isMdDown && (
						<>
							<SwipeableDrawer
								anchor="right"
								open={isFilterVisible}
								onOpen={showFilter}
								onClose={closeFilter}
								classes={{
									paper: classes.filterDrawer,
								}}
							>
								<div className={classes.jobsSearchContainer}>
									<JobApplicantsFilterSection
										handleFilterData={handleFilterData}
										toggleDrawer={toggleDrawer}
										filterData={filterData}
									/>
								</div>
							</SwipeableDrawer>
						</>
					)}
				</>
			) : (
				<SwipeableDrawer
					anchor="right"
					open={isFilterVisible}
					onOpen={showFilter}
					onClose={closeFilter}
					classes={{
						paper: classes.filterDrawer,
					}}
				>
					<div className={classes.jobsSearchContainer}>
						<JobApplicantsFilterSection
							handleFilterData={handleFilterData}
							toggleDrawer={toggleDrawer}
							filterData={filterData}
						/>
					</div>
				</SwipeableDrawer>
			)}
		</Grid>
	)
}

const JobApplicants = memo((props: any) => (
	<StoreValue
		keys={[
			'storeDispatch',
			'search.debounceQuery|storeSearchDebounceQuery',
			'search.loading|storeSearchLoading',
			'company.companySettings.jobApplicants.jobId|jobId',
		]}
	>
		<JobApplicantsView {...props} />
	</StoreValue>
))

export default JobApplicants
